import { downloadBlob, uploadBlob } from "@/lib/blobs";
import { osNotify } from "@/lib/os-notify";
import { FolderOpen, HardDrive, HardDriveDownload } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import { can, isSecretPath } from "@ide/shared";
import type { LocalFileChange, LocalProjectInfo } from "@ide/shared/desktop";
import type { ProjectSession } from "@/collab/session";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/controls";
import { Popover, PopoverContent, PopoverTrigger, Tip } from "@/components/ui/overlay";
import { desktop } from "@/desktop/bridge";
import { openLocalFolderInEditor } from "@/desktop/local-folder";
import { cn, timeAgo } from "@/lib/utils";
import { useWs } from "./context";
import { activeWorkspace, createFiles, deleteFile, readFile, writeBlobFile, writeFile } from "./file-ops";
import { logOutput } from "./output";
import { parseBlobMarker, ProjectWatch } from "./project-watch";

/**
 * "Salvar na máquina" (app desktop): o projeto fica numa pasta do computador, salva a cada
 * alteração (sua ou do time) e o que muda na pasta (terminal, npm, outro editor) volta para o
 * projeto. É a base do terminal local e de continuar programando sem internet. Desligado: só nuvem.
 */
export const localCopyAvailable = Boolean(desktop?.local);

type State = "off" | "starting" | "on" | "error";

export const useLocalCopy = create<{
  projectId: string | null;
  info: LocalProjectInfo | null;
  state: State;
  savedAt: number | null;
  error: string | null;
}>(() => ({ projectId: null, info: null, state: "off", savedAt: null, error: null }));

let active: { projectId: string; watch: ProjectWatch; off: () => void } | null = null;

/** Espera o que estiver pendente ir para o disco (antes de rodar algo no terminal local). */
export function flushLocalCopy() {
  active?.watch.flush();
}

/** Progresso de levar arquivos da pasta para o projeto (feitos, total). */
type Progress = (done: number, total: number) => void;

/**
 * Leva para o projeto o que mudou na pasta. Cada arquivo só conta quando o servidor confirma; o
 * que falhar é tentado de novo (até 3 rodadas). Devolve o que não entrou.
 */
async function applyFromDisk(
  session: ProjectSession,
  changes: LocalFileChange[],
  onProgress?: Progress,
): Promise<LocalFileChange[]> {
  if (!session.canWrite) {
    toast("Mudanças na pasta não voltam para o projeto: seu papel é só de leitura.");
    return changes;
  }
  // Pastas e arquivos de texto novos entram na árvore de uma vez; depois, o conteúdo de cada um
  // (vários ao mesmo tempo). Binários e arquivos grandes (marcador) sobem para o armazenamento.
  createFiles(
    session,
    changes.filter((c) => c.content !== null && !parseBlobMarker(c.content)).map((c) => c.path),
  );
  const projectId = activeWorkspace.current?.detail.project.id;
  let done = 0;
  const errors = new Map<string, string>();
  const runRound = async (list: LocalFileChange[]) => {
    const failed: LocalFileChange[] = [];
    let next = 0;
    const worker = async () => {
      while (next < list.length) {
        const c = list[next++]!;
        try {
          const blob = parseBlobMarker(c.content);
          if (c.content === null) deleteFile(session, c.path);
          else if (blob) await uploadFromDisk(session, projectId, c.path);
          else await writeFile(session, c.path, c.content, { confirm: true });
          errors.delete(c.path);
          onProgress?.(++done, changes.length);
        } catch (err) {
          errors.set(c.path, err instanceof Error ? err.message : String(err));
          failed.push(c);
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(12, list.length) }, worker));
    return failed;
  };
  let pending = await runRound(changes);
  for (let round = 1; round < 3 && pending.length; round++) {
    await new Promise((r) => setTimeout(r, 1500 * round));
    pending = await runRound(pending);
  }
  for (const c of pending)
    logOutput("Cópia local", `Não deu para trazer ${c.path}: ${errors.get(c.path) ?? "erro"}`, "warn");
  onProgress?.(changes.length, changes.length);
  return pending;
}

/** Avisa o que não entrou no projeto, com "Tentar de novo". */
function reportFailed(session: ProjectSession, failed: LocalFileChange[]) {
  toast.error(`${failed.length} arquivo(s) da pasta não entraram no projeto`, {
    id: "local-copy-failed",
    duration: Infinity,
    description: `${failed
      .slice(0, 3)
      .map((c) => c.path)
      .join(", ")}${failed.length > 3 ? "…" : ""} — os arquivos continuam na pasta.`,
    action: {
      label: "Tentar de novo",
      onClick: () =>
        void applyFromDisk(session, failed).then((left) => {
          if (left.length) reportFailed(session, left);
          else toast.success("Todos os arquivos da pasta estão no projeto.");
        }),
    },
  });
}

/** Binário da pasta → armazenamento do projeto → árvore (sha256 e tamanho). */
async function uploadFromDisk(session: ProjectSession, projectId: string | undefined, path: string) {
  if (!projectId || !desktop?.local.readBinary)
    throw new Error("Atualize o Kompil Desktop para enviar arquivos binários.");
  const data = await desktop.local.readBinary(projectId, path);
  if (!data) throw new Error("o arquivo não pôde ser lido na pasta");
  // Vale o sha dos bytes lidos agora (o arquivo pode ter mudado desde que foi conferido).
  const ref = await uploadBlob(projectId, data);
  writeBlobFile(session, path, ref);
}

/** Arquivos binários do projeto que faltam na pasta do computador: baixa e grava. */
async function downloadToDisk(
  projectId: string,
  items: Array<{ path: string; sha256: string; size: number }>,
) {
  let failed = 0;
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const item = items[next++]!;
      try {
        const data = await downloadBlob(projectId, item);
        await desktop?.local.writeBinary?.(projectId, item.path, data, item.sha256);
      } catch (err) {
        failed++;
        logOutput(
          "Cópia local",
          `Não deu para baixar ${item.path}: ${err instanceof Error ? err.message : String(err)}`,
          "warn",
        );
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, items.length) }, worker));
  if (items.length - failed > 0)
    logOutput("Cópia local", `${items.length - failed} arquivo(s) binário(s) do projeto salvos na pasta.`);
}

/** Resultado da primeira conferência: quantos arquivos iam para o projeto e quais não entraram. */
type SyncResult = { total: number; failed: LocalFileChange[] };

async function startSync(
  session: ProjectSession,
  projectId: string,
  onProgress?: Progress,
): Promise<SyncResult> {
  const result: SyncResult = { total: 0, failed: [] };
  if (!desktop?.local) return result;
  stopSync();
  useLocalCopy.setState({ state: "starting", error: null });
  // A árvore precisa estar carregada (do servidor ou do cache local) antes de comparar com a pasta.
  await session.whenTreeSynced(8_000).catch(() => undefined);
  const watch = new ProjectWatch(session, (changes) => {
    desktop?.local.update(projectId, changes);
    useLocalCopy.setState({ savedAt: Date.now() });
  });
  const files = await watch.collect();
  // Antes de começar: os binários que o projeto tem e a pasta não chegam já na primeira conferência.
  const offDownloads =
    desktop.local.onDownloads?.((e) => {
      if (e.projectId === projectId) void downloadToDisk(projectId, e.items);
    }) ?? (() => {});
  // Dono: os .env da pasta também sobem (no projeto, protegidos pelo "Ver .env").
  const res = await desktop.local.start(projectId, files, {
    secrets: session.role === "owner",
    unknown: watch.unloaded,
  });
  if (watch.unloaded.length)
    logOutput(
      "Cópia local",
      `${watch.unloaded.length} arquivo(s) do projeto não carregaram a tempo: a pasta não mexeu neles (confere de novo ao reabrir).`,
      "warn",
    );
  if (!res) {
    offDownloads();
    watch.stop();
    useLocalCopy.setState({ state: "off" });
    return result;
  }
  const offChanges = desktop.local.onChanges((e) => {
    if (e.projectId === projectId)
      void applyFromDisk(session, e.changes).then((left) => left.length && reportFailed(session, left));
  });
  const off = () => {
    offChanges();
    offDownloads();
  };
  active = { projectId, watch, off };
  watch.start();
  // .env mudado na pasta com o Kompil fechado (inclusive enquanto a pessoa estava sem a permissão
  // "Ver .env"): não entra — vale o do projeto, que volta para a pasta. Só o dono traz da pasta.
  const offlineEnv = session.role === "owner" ? [] : res.toProject.filter((c) => isSecretPath(c.path));
  const toProject = res.toProject.filter((c) => !offlineEnv.includes(c));
  if (offlineEnv.length) {
    const restore: LocalFileChange[] = [];
    for (const c of offlineEnv) {
      const content = await readFile(session, c.path).catch(() => null);
      if (content !== null) restore.push({ path: c.path, content });
    }
    if (restore.length) desktop.local.update(projectId, restore);
    logOutput(
      "Cópia local",
      `Alterações feitas fora do Kompil em ${offlineEnv.map((c) => c.path).join(", ")} não entraram: nos arquivos .env só valem as do dono e as feitas ao vivo por quem tem a permissão.`,
      "warn",
    );
  }
  if (res.kept?.length)
    logOutput(
      "Cópia local",
      `${res.kept.length} arquivo(s) da pasta faltavam no projeto: foram enviados de novo (nada foi apagado da pasta).`,
      "warn",
    );
  if (toProject.length) {
    const failed = await applyFromDisk(session, toProject, onProgress);
    const sent = toProject.length - failed.length;
    logOutput("Cópia local", `${sent} de ${toProject.length} arquivo(s) da pasta entraram no projeto.`);
    if (failed.length) reportFailed(session, failed);
    result.total = toProject.length;
    result.failed = failed;
  }
  if (res.conflicts.length) {
    toast("Arquivos mudaram na pasta e no projeto", {
      description: `Ficou a versão do projeto; a da pasta foi guardada como cópia de segurança: ${res.conflicts.slice(0, 3).join(", ")}`,
    });
  }
  useLocalCopy.setState({ state: "on", savedAt: Date.now() });
  return result;
}

function stopSync() {
  if (!active) return;
  active.off();
  active.watch.stop();
  desktop?.local.stop(active.projectId);
  active = null;
}

/** Liga a sincronização com a pasta enquanto o projeto está aberto (se a pessoa ligou). */
export function useLocalCopySync(session: ProjectSession, project: { id: string; name: string }) {
  useEffect(() => {
    if (!desktop?.local) return;
    let alive = true;
    useLocalCopy.setState({ projectId: project.id, info: null, state: "off", savedAt: null });
    void desktop.local.get(project.id, project.name).then((info) => {
      if (!alive) return;
      useLocalCopy.setState({ info });
      // Visualizador não sincroniza a pasta (o código não vai para o computador dele).
      if (info.enabled && can(session.role ?? null, "terminal")) {
        void startSync(session, project.id).catch((err) =>
          useLocalCopy.setState({ state: "error", error: err instanceof Error ? err.message : String(err) }),
        );
      }
    });
    return () => {
      alive = false;
      stopSync();
      useLocalCopy.setState({ projectId: null, info: null, state: "off" });
    };
  }, [session, project.id, project.name]);
}

export async function enableLocalCopy(
  session: ProjectSession,
  project: { id: string; name: string },
  choose: boolean,
) {
  if (!desktop?.local) return false;
  if (!can(session.role, "terminal")) {
    toast("Visualizadores não salvam o projeto no computador.");
    return false;
  }
  const res = await desktop.local.enable(project.id, project.name, choose);
  if (!res.ok) {
    if (res.error !== "cancelado") toast.error(res.error ?? "Não deu para ligar a cópia local.");
    return false;
  }
  useLocalCopy.setState({ info: await desktop.local.get(project.id, project.name) });
  await startSync(session, project.id);
  toast.success("Projeto salvo na sua máquina", { description: res.path });
  return true;
}

/**
 * "Abrir pasta" com um projeto aberto: a pasta escolhida (com código e subpastas) vira a pasta do
 * projeto no computador — o que ela tem sobe para o projeto e passa a ficar sincronizado — e, se
 * tiver .git, o repositório dela é ligado ao projeto.
 */
export async function openFolderInProject(
  session: ProjectSession,
  project: { id: string; name: string },
  onGit?: (remote: string, branch: string) => void,
): Promise<boolean> {
  if (!desktop?.local) return false;
  if (!can(session.role, "terminal")) {
    toast("Visualizadores não abrem pastas no projeto.");
    return false;
  }
  const toastId = "open-folder";
  // Progresso em duas etapas: ler a pasta (0–30%) e enviar ao projeto (30–100%).
  const show = (title: string, percent: number, detail: string) =>
    toast.loading(`${title} ${Math.round(percent)}%`, {
      id: toastId,
      duration: Infinity,
      description: detail,
    });
  const offRead = desktop.local.onImportProgress?.((p) => {
    if (p.phase === "listing") show("Lendo a pasta…", 0, `${p.done} arquivo(s) encontrados`);
    else if (p.total > 0)
      show("Lendo a pasta…", (p.done / p.total) * 30, `${p.done} de ${p.total} arquivo(s)`);
  });
  let picked: Awaited<ReturnType<typeof desktop.local.importFolder>>;
  try {
    picked = await desktop.local.importFolder();
  } catch (err) {
    toast.error("Não deu para ler a pasta", {
      id: toastId,
      description: err instanceof Error ? err.message : String(err),
    });
    return false;
  } finally {
    offRead?.();
  }
  if (!picked) {
    toast.dismiss(toastId);
    return false;
  }
  const count = Object.keys(picked.files).length;
  const name = picked.name;
  show(`Enviando ${name} para o projeto…`, 30, `${count} arquivo(s)`);
  try {
    stopSync();
    if (!(await desktop.local.adopt(project.id, picked.importId)))
      throw new Error("A pasta escolhida expirou. Abra de novo.");
    useLocalCopy.setState({ info: await desktop.local.get(project.id, project.name) });
    let shown = 0;
    const sync = await startSync(session, project.id, (done, total) => {
      // Atualiza o aviso no máximo a cada 1% (centenas de arquivos não travam a tela).
      const percent = 30 + (done / total) * 70;
      if (percent - shown < 1 && done !== total) return;
      shown = percent;
      show(`Enviando ${name} para o projeto…`, percent, `${done} de ${total} arquivo(s)`);
    });
    // Tudo vai: texto editável em tempo real; binários e arquivos grandes como arquivo do projeto.
    const left = picked.left ?? [];
    const asFiles = left.filter((f) => f.reason !== "limit").length;
    const overLimit = left.length - asFiles;
    const envLeft = session.role === "owner" ? 0 : Object.keys(picked.files).filter(isSecretPath).length;
    const extras = [
      asFiles ? `${asFiles} binário(s) ou grande(s) como arquivo` : "",
      envLeft ? `${envLeft} .env ficaram no computador (só o dono leva)` : "",
      overLimit ? `${overLimit} acima do limite de 10 mil arquivos` : "",
    ].filter(Boolean);
    const inProject = Math.max(0, count - envLeft + asFiles - sync.failed.length);
    const detail = `${inProject} arquivo(s) no projeto${extras.length ? ` · ${extras.join(" · ")}` : ""}.`;
    if (sync.failed.length) {
      // Não é 100%: o aviso "não entraram" (com "Tentar de novo") fica na tela.
      toast.warning(`${name} aberta no projeto, mas ${sync.failed.length} arquivo(s) faltam`, {
        id: toastId,
        duration: 10_000,
        description: `${detail} Use "Tentar de novo" no aviso ao lado.`,
      });
    } else
      toast.success(`${name} aberta no projeto (100%)`, { id: toastId, duration: 6000, description: detail });
    osNotify({
      id: `open-folder-${project.id}`,
      title: `${name} aberta no projeto ${project.name}`,
      body: detail,
    });
    if (picked.git) onGit?.(picked.git.url, picked.git.branch ?? "main");
    return true;
  } catch (err) {
    toast.error("Não deu para abrir a pasta no projeto", {
      id: toastId,
      description: err instanceof Error ? err.message : String(err),
    });
    return false;
  }
}

export async function disableLocalCopy(project: { id: string; name: string }) {
  if (!desktop?.local) return;
  stopSync();
  await desktop.local.disable(project.id);
  useLocalCopy.setState({ state: "off", info: await desktop.local.get(project.id, project.name) });
  toast("Cópia local desligada", {
    description: "O projeto segue salvo na nuvem. A pasta continua no seu computador.",
  });
}

/** Barra de título: "Salvo na máquina" / "Só na nuvem", com o liga-desliga e a pasta. */
export function LocalCopyControl() {
  const { session, detail } = useWs();
  const { info, state, savedAt, error } = useLocalCopy();
  if (!localCopyAvailable || !info) return null;
  const project = { id: detail.project.id, name: detail.project.name };
  const on = info.enabled && state === "on";
  return (
    <Popover>
      <Tip
        label={
          on
            ? "Salvando na sua máquina a cada alteração"
            : "Só na nuvem — clique para salvar também na sua máquina"
        }
      >
        <PopoverTrigger asChild>
          <button
            className={cn(
              "flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-[12.5px] font-medium transition-colors hover:bg-bg-muted",
              on ? "border-border-strong text-fg" : "border-dashed border-border-strong text-fg-muted",
            )}
            aria-label="Salvar na máquina"
          >
            {on ? <HardDriveDownload className="h-3.5 w-3.5" /> : <HardDrive className="h-3.5 w-3.5" />}
            <span className="hidden xl:inline">{on ? "Salvo na máquina" : "Só na nuvem"}</span>
            {state === "starting" ? (
              <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-fg" />
            ) : null}
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent align="end" className="w-[340px] p-4">
        <label className="flex items-start justify-between gap-3">
          <span>
            <span className="block text-[13px] font-medium text-fg">Salvar na máquina local</span>
            <span className="mt-0.5 block text-[12px] leading-relaxed text-fg-muted">
              Cada alteração (sua ou do time) é gravada numa pasta do seu computador, e o que mudar na pasta
              volta para o projeto. Base do terminal local e do trabalho sem internet.
            </span>
          </span>
          <Switch
            checked={info.enabled}
            onCheckedChange={(v) =>
              void (v ? enableLocalCopy(session, project, false) : disableLocalCopy(project))
            }
            aria-label="Salvar na máquina local"
          />
        </label>
        <div className="mt-3 rounded-lg border border-border bg-bg p-2.5 text-[12px]">
          <p className="text-fg-subtle">{info.enabled ? "Pasta" : "Vai salvar em"}</p>
          <p className="mt-0.5 break-all font-mono text-[11.5px] text-fg">{info.path ?? info.suggested}</p>
          {on && savedAt ? <p className="mt-1 text-fg-subtle">Último salvamento {timeAgo(savedAt)}</p> : null}
          {state === "error" ? <p className="mt-1 text-[var(--danger)]">{error}</p> : null}
        </div>
        <div className="mt-3 flex flex-col items-start gap-1.5">
          <Button size="sm" variant="secondary" onClick={() => void openLocalFolderInEditor()}>
            <FolderOpen className="h-3.5 w-3.5" /> Abrir pasta existente…
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void enableLocalCopy(session, project, true)}>
            Escolher pasta para salvar…
          </Button>
          {info.path ? (
            <Button size="sm" variant="ghost" onClick={() => desktop?.local.reveal(project.id)}>
              Mostrar no computador
            </Button>
          ) : null}
        </div>
        <p className="mt-1 text-[11.5px] text-fg-subtle">
          A pasta (com o código e as subpastas) entra neste projeto e fica sincronizada; com .git, o
          repositório liga sozinho.
        </p>
        <p className="mt-3 text-[11.5px] leading-relaxed text-fg-subtle">
          Ficam só no computador: <code className="font-mono">.git</code>,{" "}
          <code className="font-mono">node_modules</code>, <code className="font-mono">.venv</code>, builds,
          binários e o seu <code className="font-mono">.env</code>.
        </p>
      </PopoverContent>
    </Popover>
  );
}
